import React from "react";
import { Link } from "react-router-dom";
import { Download, Trash2, WifiOff, ChevronRight, BookOpen } from "lucide-react";
import { useOfflineLessons, removeOfflineLesson, clearOfflineLessons, useOnline } from "../lib/offline";

export default function Offline() {
  const items = useOfflineLessons();
  const online = useOnline();
  const bytes = items.reduce((n, i) => n + (i.content?.length || 0), 0);

  return (
    <div className="space-y-6" data-testid="offline-root">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-lime-400/30 bg-lime-500/10 px-3 py-1 text-xs uppercase tracking-widest text-lime-300">
          <Download size={12} /> Saved for offline
        </div>
        <h1 className="font-display mt-4 text-3xl font-bold tracking-tight sm:text-4xl">Lessons in your pocket</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-400">
          Open any lesson and tap <b className="text-slate-200">Save offline</b>. It stays on this device, so you can read it on the bus with no signal.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
          <span data-testid="offline-status" className={online ? "text-lime-300" : "text-amber-300"}>{online ? "● Online" : "● Offline — reading saved copies"}</span>
          <span className="font-mono-num" data-testid="offline-count">{items.length} saved · {(bytes / 1024).toFixed(0)} KB</span>
          {items.length > 0 && (
            <button onClick={() => window.confirm("Remove all saved lessons from this device?") && clearOfflineLessons()} data-testid="offline-clear-all" className="inline-flex items-center gap-1 text-red-300 hover:text-red-200">
              <Trash2 size={12} /> Remove all
            </button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border border-white/10 bg-black/40 p-8 text-center" data-testid="offline-empty">
          <WifiOff size={28} className="mx-auto text-slate-600" />
          <div className="mt-3 text-sm text-slate-400">Nothing saved yet.</div>
          <Link to="/curriculum" className="mt-3 inline-flex items-center gap-2 text-sm text-cyan-300 hover:underline">Browse the curriculum <ChevronRight size={14} /></Link>
        </div>
      ) : (
        <ul className="divide-y divide-white/5 rounded-md border border-white/10 bg-black/40">
          {items.map(({ lesson, saved_at }) => (
            <li key={`${lesson.year}-${lesson.order}`} className="flex items-center gap-3 px-3 py-3 sm:px-4" data-testid={`offline-item-${lesson.year}-${lesson.order}`}>
              <Link to={`/curriculum/${lesson.year}/${lesson.order}`} className="flex min-w-0 flex-1 items-center gap-3" data-testid={`offline-open-${lesson.year}-${lesson.order}`}>
                <BookOpen size={16} className="shrink-0 text-lime-300" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-white">{lesson.title}</div>
                  <div className="truncate text-[10px] uppercase tracking-widest text-slate-500">Y{lesson.year} · #{String(lesson.order).padStart(2, "0")} · {lesson.unit} · saved {new Date(saved_at).toLocaleDateString()}</div>
                </div>
              </Link>
              <button onClick={() => removeOfflineLesson(lesson.year, lesson.order)} data-testid={`offline-remove-${lesson.year}-${lesson.order}`} title="Remove" className="icon-btn shrink-0 rounded-md p-2 text-slate-500 hover:bg-red-500/10 hover:text-red-300">
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
